// Email Admin Page - Manage email account integrations (Gmail + Outlook)

import { useEffect, useState, useCallback } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Card, CardHeader, CardBody, Button, StatusBadge } from '../components/common';
import { formatDate, formatRelativeTime } from '../utils/dateFormatters';
import { emailService } from '../services/email';
import type { EmailAccount, EmailAccountDetail, EmailStats, EmailTestResult, EmailProvider } from '../types/api';
import { MailIcon, PlusIcon, RefreshIcon, CheckIcon, XIcon, TrashIcon, StarIcon } from '../components/icons';
import styles from './GmailAdminPage.module.css';

// Status badge component
function AccountStatusBadge({ status }: { status: string }) {
  const variant = status === 'enabled' ? 'success'
    : status === 'authenticated' ? 'info'
    : status === 'token_expired' ? 'warning'
    : status === 'error' ? 'error'
    : status === 'disabled' ? 'neutral'
    : 'info';
  return (
    <StatusBadge variant={variant}>
      {status.replace('_', ' ')}
    </StatusBadge>
  );
}

// Provider badge component
function ProviderBadge({ provider }: { provider: string }) {
  return (
    <span className={`${styles.providerBadge} ${provider === 'gmail' ? styles.gmail : styles.outlook}`}>
      {provider}
    </span>
  );
}

// Auth method badge component
function AuthMethodBadge({ authMethod }: { authMethod: string }) {
  const isDelegation = authMethod === 'delegation';
  return (
    <span className={`${styles.providerBadge} ${isDelegation ? styles.delegation : styles.oauth}`}>
      {isDelegation ? 'delegation' : 'oauth'}
    </span>
  );
}

export function GmailAdminPage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const [accounts, setAccounts] = useState<EmailAccount[]>([]);
  const [stats, setStats] = useState<EmailStats | null>(null);
  const [loading, setLoading] = useState(true);
  const [selectedAccount, setSelectedAccount] = useState<EmailAccountDetail | null>(null);
  const [actionLoading, setActionLoading] = useState<string | null>(null);
  const [testResult, setTestResult] = useState<EmailTestResult | null>(null);
  const [showDeleteDialog, setShowDeleteDialog] = useState<string | null>(null);
  const [showAddDialog, setShowAddDialog] = useState(false);
  const [addProvider, setAddProvider] = useState<EmailProvider>('gmail');
  const [syncResult, setSyncResult] = useState<Record<string, unknown> | null>(null);
  const [syncAllResult, setSyncAllResult] = useState<Record<string, unknown> | null>(null);

  // Check for OAuth callback params
  const success = searchParams.get('success');
  const error = searchParams.get('error');
  const email = searchParams.get('email');
  const provider = searchParams.get('provider');

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const [accountsData, statsData] = await Promise.all([
        emailService.getAccounts(),
        emailService.getStats(),
      ]);
      setAccounts(accountsData);
      setStats(statsData);

      // Clear success/error params after showing
      if (success || error) {
        setTimeout(() => {
          searchParams.delete('success');
          searchParams.delete('error');
          searchParams.delete('email');
          searchParams.delete('provider');
          setSearchParams(searchParams);
        }, 5000);
      }
    } catch (err) {
      console.error('Failed to fetch email data:', err);
    } finally {
      setLoading(false);
    }
  }, [success, error, searchParams, setSearchParams]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const handleSelectAccount = async (account: EmailAccount) => {
    try {
      const detail = await emailService.getAccount(account.id);
      setSelectedAccount(detail);
      setTestResult(null);
    } catch (err) {
      console.error('Failed to fetch account details:', err);
    }
  };

  const handleAddAccount = async (purpose: 'general' | 'orders_inbox') => {
    setActionLoading('add');
    try {
      const response = addProvider === 'gmail'
        ? await emailService.startGmailAuth(purpose)
        : await emailService.startOutlookAuth(purpose);
      // Redirect to OAuth provider
      window.location.href = response.authorization_url;
    } catch (err) {
      console.error('Failed to start OAuth:', err);
      setActionLoading(null);
    }
  };

  const handleEnable = async (accountId: string) => {
    setActionLoading(accountId);
    try {
      await emailService.enableAccount(accountId);
      await fetchData();
      if (selectedAccount?.id === accountId) {
        const detail = await emailService.getAccount(accountId);
        setSelectedAccount(detail);
      }
    } catch (err) {
      console.error('Failed to enable account:', err);
    } finally {
      setActionLoading(null);
    }
  };

  const handleDisable = async (accountId: string) => {
    setActionLoading(accountId);
    try {
      await emailService.disableAccount(accountId);
      await fetchData();
      if (selectedAccount?.id === accountId) {
        const detail = await emailService.getAccount(accountId);
        setSelectedAccount(detail);
      }
    } catch (err) {
      console.error('Failed to disable account:', err);
    } finally {
      setActionLoading(null);
    }
  };

  const handleSetPrimary = async (accountId: string) => {
    setActionLoading(accountId);
    try {
      await emailService.setPrimary(accountId);
      await fetchData();
      if (selectedAccount?.id === accountId) {
        const detail = await emailService.getAccount(accountId);
        setSelectedAccount(detail);
      }
    } catch (err) {
      console.error('Failed to set primary:', err);
    } finally {
      setActionLoading(null);
    }
  };

  const handleDelete = async (accountId: string) => {
    setActionLoading(accountId);
    try {
      await emailService.deleteAccount(accountId);
      await fetchData();
      if (selectedAccount?.id === accountId) {
        setSelectedAccount(null);
      }
      setShowDeleteDialog(null);
    } catch (err) {
      console.error('Failed to delete account:', err);
    } finally {
      setActionLoading(null);
    }
  };

  const pollSyncTask = async (
    taskId: string,
    onComplete: (result: Record<string, unknown>) => void,
    onError: (error: string) => void,
  ) => {
    const MAX_POLLS = 120; // 2 minutes at 1s interval
    for (let i = 0; i < MAX_POLLS; i++) {
      await new Promise((r) => setTimeout(r, 1000));
      try {
        const status = await emailService.getSyncStatus(taskId);
        if (status.state === 'SUCCESS') {
          onComplete(status.result ?? { status: 'completed' });
          return;
        }
        if (status.state === 'FAILURE') {
          onError(status.error ?? 'Sync task failed');
          return;
        }
        // PENDING, STARTED, RETRY — keep polling
      } catch {
        // network blip — keep trying
      }
    }
    onError('Sync timed out. It may still be running in the background.');
  };

  const handleSync = async (accountId: string, fullSync: boolean = false) => {
    setActionLoading(`sync-${accountId}`);
    setSyncResult(null);
    try {
      const dispatch = await emailService.syncAccount(accountId, fullSync);
      const taskId = dispatch.task_id as string;
      setSyncResult({ status: 'syncing', message: 'Sync started…' });

      await pollSyncTask(
        taskId,
        async (result) => {
          setSyncResult(result);
          await fetchData();
          if (selectedAccount?.id === accountId) {
            const detail = await emailService.getAccount(accountId);
            setSelectedAccount(detail);
          }
          setActionLoading(null);
        },
        (error) => {
          setSyncResult({ status: 'error', error });
          setActionLoading(null);
        },
      );
    } catch (err) {
      console.error('Failed to dispatch sync:', err);
      setSyncResult({ status: 'error', error: 'Failed to start sync. Check account status and try again.' });
      setActionLoading(null);
    }
  };

  const handleSyncAll = async () => {
    setActionLoading('sync-all');
    setSyncAllResult(null);
    try {
      const dispatch = await emailService.syncAll();
      const taskId = dispatch.task_id as string;
      setSyncAllResult({ status: 'syncing', message: 'Sync started for all accounts…' });

      await pollSyncTask(
        taskId,
        async (result) => {
          setSyncAllResult(result);
          await fetchData();
          setActionLoading(null);
        },
        (error) => {
          setSyncAllResult({ status: 'error', error });
          setActionLoading(null);
        },
      );
    } catch (err) {
      console.error('Failed to dispatch sync all:', err);
      setSyncAllResult({ status: 'error', error: 'Failed to start sync.' });
      setActionLoading(null);
    }
  };

  const handleTest = async (accountId: string, sendEmail: boolean = false) => {
    setActionLoading(`test-${accountId}`);
    setTestResult(null);
    try {
      const result = await emailService.testAccount(accountId, sendEmail);
      setTestResult(result);
    } catch (err) {
      console.error('Failed to test account:', err);
      setTestResult({
        account_id: accountId,
        email: selectedAccount?.email || '',
        provider: selectedAccount?.provider || 'gmail',
        health: { status: 'error', error: 'Test failed' },
      });
    } finally {
      setActionLoading(null);
    }
  };

  return (
    <div className={styles.container}>
      {/* Success/Error Toast */}
      {(success || error) && (
        <div className={`${styles.toast} ${error ? styles.error : styles.success}`}>
          {success ? (
            <>
              <CheckIcon className={styles.toastIcon} />
              {provider ? `${provider.charAt(0).toUpperCase() + provider.slice(1)} account` : 'Account'} {email} connected successfully!
            </>
          ) : (
            <>
              <XIcon className={styles.toastIcon} />
              OAuth failed: {error}
            </>
          )}
        </div>
      )}

      {/* Sync All Result Toast */}
      {syncAllResult && (
        <div className={`${styles.toast} ${syncAllResult.status === 'error' ? styles.error : syncAllResult.status === 'syncing' ? styles.info : styles.success}`}>
          {syncAllResult.status === 'error' ? (
            <>
              <XIcon className={styles.toastIcon} />
              {String(syncAllResult.error || 'Sync failed')}
            </>
          ) : syncAllResult.status === 'syncing' ? (
            <>
              <RefreshIcon className={`${styles.toastIcon} ${styles.spinning}`} />
              {String(syncAllResult.message || 'Syncing…')}
            </>
          ) : (
            <>
              <CheckIcon className={styles.toastIcon} />
              Synced {String(syncAllResult.accounts_synced || 0)} account(s)
            </>
          )}
        </div>
      )}

      {/* Header */}
      <div className={styles.header}>
        <div className={styles.headerLeft}>
          <h1 className={styles.title}>
            <MailIcon className={styles.titleIcon} />
            Email Integration
          </h1>
          {stats && (
            <span className={styles.subtitle}>
              {stats.total} account{stats.total !== 1 ? 's' : ''} connected
              {stats.by_provider && Object.keys(stats.by_provider).length > 1 && (
                <> ({Object.entries(stats.by_provider).map(([p, c]) => `${c} ${p}`).join(', ')})</>
              )}
            </span>
          )}
        </div>
        <div className={styles.headerActions}>
          <Button variant="outline" onClick={fetchData} disabled={loading}>
            <RefreshIcon />
            Refresh
          </Button>
          <Button
            variant="outline"
            onClick={handleSyncAll}
            disabled={actionLoading === 'sync-all' || accounts.length === 0}
          >
            <RefreshIcon />
            {actionLoading === 'sync-all' ? 'Syncing...' : 'Sync All Accounts'}
          </Button>
          <Button variant="primary" onClick={() => setShowAddDialog(true)}>
            <PlusIcon />
            Add Email Account
          </Button>
        </div>
      </div>

      {/* Stats Cards */}
      {stats && (
        <div className={styles.statsGrid}>
          <Card className={styles.statCard}>
            <CardBody>
              <div className={styles.statValue}>{stats.total}</div>
              <div className={styles.statLabel}>Total Accounts</div>
            </CardBody>
          </Card>
          <Card className={`${styles.statCard} ${styles.enabled}`}>
            <CardBody>
              <div className={styles.statValue}>{stats.by_status.enabled || 0}</div>
              <div className={styles.statLabel}>Enabled</div>
            </CardBody>
          </Card>
          <Card className={`${styles.statCard} ${styles.authenticated}`}>
            <CardBody>
              <div className={styles.statValue}>{stats.by_status.authenticated || 0}</div>
              <div className={styles.statLabel}>Authenticated</div>
            </CardBody>
          </Card>
          <Card className={`${styles.statCard} ${styles.error}`}>
            <CardBody>
              <div className={styles.statValue}>
                {(stats.by_status.token_expired || 0) + (stats.by_status.error || 0)}
              </div>
              <div className={styles.statLabel}>Needs Attention</div>
            </CardBody>
          </Card>
        </div>
      )}

      {/* Content */}
      <div className={styles.content}>
        {/* Account List */}
        <div className={styles.accountList}>
          {loading ? (
            <div className={styles.loading}>Loading accounts...</div>
          ) : accounts.length === 0 ? (
            <div className={styles.empty}>
              <MailIcon className={styles.emptyIcon} />
              <p>No email accounts connected</p>
              <p className={styles.emptyHint}>
                Click "Add Email Account" to connect Gmail or Outlook
              </p>
            </div>
          ) : (
            accounts.map((account) => (
              <div
                key={account.id}
                className={`${styles.accountCard} ${selectedAccount?.id === account.id ? styles.selected : ''}`}
                onClick={() => handleSelectAccount(account)}
              >
                <div className={styles.accountCardHeader}>
                  <div className={styles.accountEmail}>
                    {account.is_primary && (
                      <StarIcon className={styles.primaryIcon} filled />
                    )}
                    {account.email}
                    <ProviderBadge provider={account.provider} />
                    <AuthMethodBadge authMethod={account.auth_method} />
                  </div>
                  <AccountStatusBadge status={account.status} />
                </div>
                <div className={styles.accountMeta}>
                  <span>{account.display_name || 'No display name'}</span>
                  <span className={styles.separator}>|</span>
                  <span>{account.purpose.replace('_', ' ')}</span>
                </div>
                <div className={styles.accountFooter}>
                  <span className={styles.lastSync}>
                    Last sync: {formatRelativeTime(account.last_sync_at)}
                  </span>
                  {account.sync_errors > 0 && (
                    <span className={styles.syncErrors}>
                      {account.sync_errors} errors
                    </span>
                  )}
                </div>
              </div>
            ))
          )}
        </div>

        {/* Detail Panel */}
        <div className={styles.detailPanel}>
          {selectedAccount ? (
            <Card className={styles.detailCard}>
              <CardHeader>
                <div className={styles.detailHeader}>
                  <div className={styles.detailTitle}>
                    {selectedAccount.is_primary && (
                      <StarIcon className={styles.primaryIcon} filled />
                    )}
                    {selectedAccount.email}
                  </div>
                  <button
                    className={styles.closeButton}
                    onClick={() => setSelectedAccount(null)}
                  >
                    <XIcon />
                  </button>
                </div>
              </CardHeader>
              <CardBody>
                <div className={styles.detailBadges}>
                  <ProviderBadge provider={selectedAccount.provider} />
                  <AuthMethodBadge authMethod={selectedAccount.auth_method} />
                  <AccountStatusBadge status={selectedAccount.status} />
                  <span className={styles.purposeBadge}>
                    {selectedAccount.purpose.replace('_', ' ')}
                  </span>
                </div>

                {selectedAccount.auth_method === 'delegation' && (
                  <div className={styles.delegationInfo}>
                    Domain-wide delegation — no OAuth tokens needed
                    {selectedAccount.sync_after && (
                      <span> | Sync after: {formatDate(selectedAccount.sync_after)}</span>
                    )}
                  </div>
                )}

                <div className={styles.detailMeta}>
                  <div className={styles.metaItem}>
                    <span className={styles.metaLabel}>Provider</span>
                    <span className={styles.metaValue}>
                      {selectedAccount.provider === 'gmail' ? 'Google Gmail' : 'Microsoft Outlook'}
                    </span>
                  </div>
                  <div className={styles.metaItem}>
                    <span className={styles.metaLabel}>Display Name</span>
                    <span className={styles.metaValue}>
                      {selectedAccount.display_name || 'Not set'}
                    </span>
                  </div>
                  <div className={styles.metaItem}>
                    <span className={styles.metaLabel}>Connected By</span>
                    <span className={styles.metaValue}>
                      {selectedAccount.connected_by || 'Unknown'}
                    </span>
                  </div>
                  <div className={styles.metaItem}>
                    <span className={styles.metaLabel}>Connected At</span>
                    <span className={styles.metaValue}>
                      {formatDate(selectedAccount.connected_at)}
                    </span>
                  </div>
                  <div className={styles.metaItem}>
                    <span className={styles.metaLabel}>Last Sync</span>
                    <span className={styles.metaValue}>
                      {formatDate(selectedAccount.last_sync_at)}
                    </span>
                  </div>
                  {selectedAccount.auth_method !== 'delegation' && (
                    <>
                      <div className={styles.metaItem}>
                        <span className={styles.metaLabel}>Token Expiry</span>
                        <span className={styles.metaValue}>
                          {formatDate(selectedAccount.token_expiry)}
                        </span>
                      </div>
                      <div className={styles.metaItem}>
                        <span className={styles.metaLabel}>Has Refresh Token</span>
                        <span className={styles.metaValue}>
                          {selectedAccount.has_refresh_token ? 'Yes' : 'No'}
                        </span>
                      </div>
                    </>
                  )}
                  {selectedAccount.last_error && (
                    <div className={styles.metaItem}>
                      <span className={styles.metaLabel}>Last Error</span>
                      <span className={`${styles.metaValue} ${styles.errorText}`}>
                        {selectedAccount.last_error}
                      </span>
                    </div>
                  )}
                </div>

                {/* Test Results */}
                {testResult && (
                  <div className={styles.testResults}>
                    <h4>Test Results</h4>
                    <div className={`${styles.testStatus} ${testResult.health.status === 'healthy' ? styles.healthy : styles.unhealthy}`}>
                      {testResult.health.status === 'healthy' ? (
                        <>
                          <CheckIcon /> Connection Healthy
                        </>
                      ) : (
                        <>
                          <XIcon /> Connection Failed
                        </>
                      )}
                    </div>
                    {testResult.health.status === 'healthy' && (
                      <div className={styles.testDetails}>
                        {testResult.health.messages_total !== undefined && (
                          <span>Messages: {testResult.health.messages_total}</span>
                        )}
                        {testResult.health.threads_total !== undefined && (
                          <span>Threads: {testResult.health.threads_total}</span>
                        )}
                      </div>
                    )}
                    {testResult.health.error && (
                      <div className={styles.testError}>{testResult.health.error}</div>
                    )}
                    {testResult.test_email && (
                      <div className={`${styles.testEmail} ${testResult.test_email.status === 'success' ? styles.success : styles.error}`}>
                        {testResult.test_email.status === 'success' ? (
                          <>Test email sent to {testResult.test_email.sent_to}</>
                        ) : (
                          <>Failed to send test email: {testResult.test_email.error}</>
                        )}
                      </div>
                    )}
                  </div>
                )}

                {/* Sync Results */}
                {syncResult && (
                  <div className={styles.testResults}>
                    <h4>Sync Results</h4>
                    <div className={`${styles.testStatus} ${syncResult.status === 'error' ? styles.unhealthy : syncResult.status === 'syncing' ? '' : styles.healthy}`}>
                      {syncResult.status === 'error' ? (
                        <>
                          <XIcon /> Sync Failed
                        </>
                      ) : syncResult.status === 'syncing' ? (
                        <>
                          <RefreshIcon className={styles.spinning} /> Syncing…
                        </>
                      ) : (
                        <>
                          <CheckIcon /> Sync Completed
                        </>
                      )}
                    </div>
                    {syncResult.status !== 'error' && syncResult.status !== 'syncing' && (
                      <div className={styles.syncStats}>
                        <div className={styles.syncStatItem}>
                          <span className={styles.syncStatValue}>{String(syncResult.emails_fetched ?? 0)}</span>
                          <span className={styles.syncStatLabel}>Fetched</span>
                        </div>
                        <div className={styles.syncStatItem}>
                          <span className={styles.syncStatValue}>{String(syncResult.emails_new ?? 0)}</span>
                          <span className={styles.syncStatLabel}>New</span>
                        </div>
                        <div className={styles.syncStatItem}>
                          <span className={styles.syncStatValue}>{String(syncResult.emails_skipped ?? 0)}</span>
                          <span className={styles.syncStatLabel}>Skipped</span>
                        </div>
                      </div>
                    )}
                    {syncResult.error != null && (
                      <div className={styles.testError}>{String(syncResult.error)}</div>
                    )}
                  </div>
                )}

                {/* Actions */}
                <div className={styles.detailActions}>
                  <Button
                    variant="primary"
                    onClick={() => handleSync(selectedAccount.id, false)}
                    disabled={
                      actionLoading === `sync-${selectedAccount.id}` ||
                      selectedAccount.status !== 'enabled'
                    }
                  >
                    <RefreshIcon />
                    {actionLoading === `sync-${selectedAccount.id}` ? 'Syncing...' : 'Sync Now'}
                  </Button>
                  <Button
                    variant="outline"
                    onClick={() => handleTest(selectedAccount.id, false)}
                    disabled={actionLoading === `test-${selectedAccount.id}`}
                  >
                    {actionLoading === `test-${selectedAccount.id}` ? 'Testing...' : 'Test Connection'}
                  </Button>
                  <Button
                    variant="outline"
                    onClick={() => handleTest(selectedAccount.id, true)}
                    disabled={actionLoading === `test-${selectedAccount.id}`}
                  >
                    Send Test Email
                  </Button>
                </div>

                <div className={styles.detailActions}>
                  {selectedAccount.status === 'authenticated' && (
                    <Button
                      variant="primary"
                      onClick={() => handleEnable(selectedAccount.id)}
                      disabled={actionLoading === selectedAccount.id}
                    >
                      Enable Account
                    </Button>
                  )}
                  {selectedAccount.status === 'enabled' && (
                    <Button
                      variant="outline"
                      onClick={() => handleDisable(selectedAccount.id)}
                      disabled={actionLoading === selectedAccount.id}
                    >
                      Disable Account
                    </Button>
                  )}
                  {selectedAccount.status === 'disabled' && (
                    <Button
                      variant="primary"
                      onClick={() => handleEnable(selectedAccount.id)}
                      disabled={actionLoading === selectedAccount.id}
                    >
                      Re-enable Account
                    </Button>
                  )}
                  {!selectedAccount.is_primary && (
                    <Button
                      variant="outline"
                      onClick={() => handleSetPrimary(selectedAccount.id)}
                      disabled={actionLoading === selectedAccount.id}
                    >
                      <StarIcon />
                      Set as Primary
                    </Button>
                  )}
                  <Button
                    variant="outline"
                    className={styles.deleteButton}
                    onClick={() => setShowDeleteDialog(selectedAccount.id)}
                    disabled={actionLoading === selectedAccount.id}
                  >
                    <TrashIcon />
                    Delete
                  </Button>
                </div>
              </CardBody>
            </Card>
          ) : (
            <div className={styles.noSelection}>
              <MailIcon className={styles.noSelectionIcon} />
              <p>Select an account to view details</p>
            </div>
          )}
        </div>
      </div>

      {/* Add Account Dialog */}
      {showAddDialog && (
        <div className={styles.dialogOverlay} onClick={() => setShowAddDialog(false)}>
          <div className={styles.dialog} onClick={(e) => e.stopPropagation()}>
            <div className={styles.dialogHeader}>
              <MailIcon className={styles.dialogIcon} />
              <h3>Connect Email Account</h3>
            </div>

            {/* Provider Selection */}
            <p>Select your email provider:</p>
            <div className={styles.providerSelect}>
              <button
                className={`${styles.providerOption} ${addProvider === 'gmail' ? styles.selected : ''}`}
                onClick={() => setAddProvider('gmail')}
              >
                <strong>Gmail</strong>
                <span>Google Workspace</span>
              </button>
              <button
                className={`${styles.providerOption} ${addProvider === 'outlook' ? styles.selected : ''}`}
                onClick={() => setAddProvider('outlook')}
              >
                <strong>Outlook</strong>
                <span>Microsoft 365</span>
              </button>
            </div>

            {/* Purpose Selection */}
            <p>Select the purpose:</p>
            <div className={styles.purposeOptions}>
              <button
                className={styles.purposeOption}
                onClick={() => handleAddAccount('orders_inbox')}
                disabled={actionLoading === 'add'}
              >
                <strong>Orders Inbox</strong>
                <span>Primary inbox for receiving customer orders</span>
              </button>
              <button
                className={styles.purposeOption}
                onClick={() => handleAddAccount('general')}
                disabled={actionLoading === 'add'}
              >
                <strong>General</strong>
                <span>General purpose account</span>
              </button>
            </div>
            <div className={styles.dialogActions}>
              <Button variant="outline" onClick={() => setShowAddDialog(false)}>
                Cancel
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Delete Confirmation Dialog */}
      {showDeleteDialog && (
        <div className={styles.dialogOverlay} onClick={() => setShowDeleteDialog(null)}>
          <div className={styles.dialog} onClick={(e) => e.stopPropagation()}>
            <div className={styles.dialogHeader}>
              <TrashIcon className={styles.dialogIcon} />
              <h3>Delete Email Account</h3>
            </div>
            <p>
              Are you sure you want to delete this email account? This will revoke
              the OAuth tokens and remove all associated data.
            </p>
            <div className={styles.dialogActions}>
              <Button variant="outline" onClick={() => setShowDeleteDialog(null)}>
                Cancel
              </Button>
              <Button
                variant="primary"
                className={styles.deleteConfirmButton}
                onClick={() => handleDelete(showDeleteDialog)}
                disabled={actionLoading === showDeleteDialog}
              >
                {actionLoading === showDeleteDialog ? 'Deleting...' : 'Delete Account'}
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
